// One place to turn a caught `unknown` into the sentence a human should read.
//
// THE FAILURE THIS EXISTS FOR. supabase-js does not reject with an `Error`. It rejects
// with a **PostgrestError** — a plain object `{ message, details, hint, code }` — so the
// idiom that looks right everywhere else:
//
//     e instanceof Error ? e.message : String(e)
//
// takes the `String(e)` branch and renders **"[object Object]"**, throwing away the only
// part that mattered. Measured on the mig-223 lead-source delete guard, whose entire
// purpose was showing the database's own English sentence to the user. A guard that
// raises a careful message, surfaced as "[object Object]", is a guard that did nothing.
//
// FunctionsHttpError (edge-function invokes) is the same shape hazard from the other
// direction: it IS an Error, but its `.message` is the generic
// "Edge Function returned a non-2xx status code" while the useful text sits in a JSON
// body the caller has to read itself. That one is not solvable here — read the body at
// the call site — but the object-shaped case is, and it is the common one.
//
// Dependency-free on purpose (no React, no supabase-js) so `node --test` imports it
// directly — the src/lib/authLinks.ts idiom.

/** The shape supabase-js rejects with. Structural, not a type import: pulling the real
 *  PostgrestError type in would make this module depend on supabase-js and drop it out
 *  of the pure-module test harness. */
interface MessageBearing {
  message?: unknown
  details?: unknown
  hint?: unknown
  code?: unknown
}

function firstString(...candidates: unknown[]): string | null {
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim() !== '') return c.trim()
  }
  return null
}

/**
 * The best available human-readable sentence for a caught value.
 *
 * Order is deliberate: `message` first (both Error and PostgrestError carry it), then
 * `details`/`hint`, which is where Postgres puts the useful half of some errors while
 * `message` stays generic. Falls back to a caller-supplied sentence rather than to
 * `String(e)` — "[object Object]" is never worth showing to anyone.
 */
export function errorMessage(e: unknown, fallback = 'Something went wrong.'): string {
  if (typeof e === 'string') return firstString(e) ?? fallback
  if (e instanceof Error) return firstString(e.message) ?? fallback

  if (e && typeof e === 'object') {
    const o = e as MessageBearing
    const found = firstString(o.message, o.details, o.hint)
    if (found) return found
  }

  return fallback
}

/**
 * True when the error carries a specific Postgres SQLSTATE. Lets a caller special-case
 * its own raises — 42501 (not authorized), 22023 (invalid parameter), 23514 (CHECK) —
 * WITHOUT substring-matching English, which breaks the moment the sentence is reworded.
 */
export function hasSqlState(e: unknown, code: string): boolean {
  if (!e || typeof e !== 'object') return false
  return (e as MessageBearing).code === code
}
